import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Select, Store } from '@ngxs/store';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { ChatUsersState } from 'app/library/all-users/models/chat-users.state';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { SortAllUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';
import { MatStepper } from '@angular/material/stepper';
import { ModuleManagerPermissions } from 'app/library/module-user/models/module-manager-permissions';
import { ModuleUserPermissions } from 'app/library/module-user/enums/module-user-permissions.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-add-managers-dialog',
    templateUrl: './add-managers-dialog.component.html',
    styleUrls: ['./add-managers-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddManagersDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    entitie$: Observable<User[]>;

    @Input()
    entityId: string;

    @Input()
    isRoleLabelVisible = false;

    @Input()
    title: string;

    @Input()
    dataType: string;

    @Input()
    role: string;

    @Input()
    moduleId: string;

    onConfirm = new EventEmitter<User>();

    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    list: User[] = [];
    hideEmail: boolean;
    formGroup: FormGroup;

    moduleManagerPermissions = ModuleManagerPermissions;
    filteredPermissions = ModuleManagerPermissions.all.filter((permission) => permission.value !== ModuleUserPermissions.course_booking_management);

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(ChatUsersState.getTotalCount)
    totalCountChatUsers$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    get permissionsFormArray(): FormArray {
        return this.formGroup.get('permissions') as FormArray;
    }

    getSelectedPermissions(): ModuleUserPermissions[] {
        return this.permissionsFormArray.controls
            .map((control, i) => (control.value ? this.moduleManagerPermissions.all[i].value : null))
            .filter((value): value is ModuleUserPermissions => value !== null);
    }

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddManagersDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private fb: FormBuilder,
        private _translateService: TranslateService
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    async ngOnInit(): Promise<void> {
        this.formGroup = this.fb.group({
            permissions: this.fb.array([]),
        });

        this.filteredPermissions.forEach(() => this.permissionsFormArray.push(new FormControl(true)));

        // Keep dialog search local — do not write AllUsers searchTerm/sessionStorage (shared with Admin/Users).
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe(() => {
            this.loadUsersPage(true);
        });

        this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);

        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.loadUsersPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            if (this.hideEmail) {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
            } else {
                this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
            }
        });
    }

    removeFromList(user: User): void {
        this.list = [...this.list.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.list = [...this.list, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    saveSelected(): void {
        this.dialogRef.close({ list: this.list, permissions: this.getSelectedPermissions() });
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    goForward(stepper: MatStepper): void {
        stepper.next();
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        if (stepper.selectedIndex === 0) {
            this.title = this._translateService.instant('GENERAL.LABEl.CHOOSE_MANAGERS');
        } else if (stepper.selectedIndex === 1) {
            this.title = this._translateService.instant('ADD_MANAGER.DIALOG.TITLE', {
                usersCount: this.list.length,
            });
        }
    }

    async loadUsersPage(resetPageIndex = false, isUnassignedSelected = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                this.dataType,
                [],
                this.entityId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                isUnassignedSelected,
                this.role,
                false,
                this.moduleId
            );
        }
    }
}
